/**
 * Feature Flag Context
 *
 * Optimized React context for feature flags with proper performance and error handling.
 * Implements memoization, cleanup, and efficient re-rendering.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  featureFlagService,
  FeatureFlags,
} from "../services/featureFlagService";

interface FeatureFlagContextType {
  flags: FeatureFlags;
  loading: boolean;
  error: string | null;
  isEnabled: (flag: keyof FeatureFlags) => boolean;
  updateFlags: (flags: Partial<FeatureFlags>) => Promise<void>;
  refresh: () => Promise<void>;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(
  undefined
);

interface FeatureFlagProviderProps {
  children: React.ReactNode;
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({
  children,
}) => {
  // Defaults are available immediately — do not block the route tree on refresh.
  const [flags, setFlags] = useState<FeatureFlags>(() =>
    featureFlagService.getAllFlags()
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  const loadGenerationRef = useRef(0);
  const updateInProgressRef = useRef(false);

  const loadFlags = useCallback(async () => {
    if (!isMountedRef.current) return;

    const loadId = ++loadGenerationRef.current;
    setLoading(true);
    try {
      setError(null);

      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const timeout = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error("Feature flags timeout")),
          3000
        );
      });

      try {
        await Promise.race([featureFlagService.initialize(), timeout]);
      } finally {
        if (timeoutId !== undefined) clearTimeout(timeoutId);
      }

      if (!isMountedRef.current || loadId !== loadGenerationRef.current) return;

      const loadedFlags = featureFlagService.getAllFlags();
      setFlags(loadedFlags);
    } catch {
      if (!isMountedRef.current || loadId !== loadGenerationRef.current) return;

      // Silently fail - we already have defaults
      try {
        const fallbackFlags = featureFlagService.getAllFlags();
        if (fallbackFlags && Object.keys(fallbackFlags).length > 0) {
          setFlags(fallbackFlags);
          setError(null);
        }
      } catch {
        // Silently use defaults - already set in initial state
      }
    } finally {
      // Always clear loading for the latest in-flight load (Strict Mode safe).
      if (isMountedRef.current && loadId === loadGenerationRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const updateFlags = useCallback(async (newFlags: Partial<FeatureFlags>) => {
    if (updateInProgressRef.current) {
      return;
    }

    updateInProgressRef.current = true;

    try {
      setError(null);

      // Optimistic update
      setFlags((prev) => ({ ...prev, ...newFlags } as FeatureFlags));

      await featureFlagService.updateFlags(newFlags);

      if (!isMountedRef.current) return;

      const updatedFlags = featureFlagService.getAllFlags();
      setFlags(updatedFlags);
    } catch (err) {
      if (!isMountedRef.current) return;

      const errorMessage =
        err instanceof Error ? err.message : "Failed to update feature flags";
      setError(errorMessage);

      // Revert optimistic update on error
      const currentFlags = featureFlagService.getAllFlags();
      setFlags(currentFlags);

      throw err;
    } finally {
      updateInProgressRef.current = false;
    }
  }, []);

  const isEnabled = useCallback(
    (flag: keyof FeatureFlags): boolean => {
      return flags[flag] || false;
    },
    [flags]
  );

  const refresh = useCallback(async () => {
    await featureFlagService.refresh();
    if (isMountedRef.current) {
      const refreshedFlags = featureFlagService.getAllFlags();
      setFlags(refreshedFlags);
    }
  }, []);

  // Initialize on mount (non-blocking - loads in background)
  useEffect(() => {
    // Load flags in background without blocking UI
    // We already have defaults from initial state
    loadFlags();
  }, [loadFlags]);

  // Subscribe to service updates
  useEffect(() => {
    const unsubscribe = featureFlagService.subscribe((newFlags) => {
      if (isMountedRef.current) {
        setFlags(newFlags);
      }
    });

    return unsubscribe;
  }, []);

  // Track mount for async updates (reset true on mount — Strict Mode safe)
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue: FeatureFlagContextType = useMemo(
    () => ({
      flags,
      loading,
      error,
      isEnabled,
      updateFlags,
      refresh,
    }),
    [flags, loading, error, isEnabled, updateFlags, refresh]
  );

  return (
    <FeatureFlagContext.Provider value={contextValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlagContextType => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagProvider"
    );
  }
  return context;
};

/**
 * Feature-flag read for a single flag with a fail-closed default.
 * Unlike `useFeatureFlags` (which throws when used outside a provider), this tolerates the
 * absence of a `FeatureFlagProvider` for isolated unit renders and returns `false` — keeping
 * gated features off. The app always mounts the provider, so its value is authoritative there.
 */
export const useFeatureFlag = (flag: keyof FeatureFlags): boolean => {
  const context = useContext(FeatureFlagContext);
  if (!context) return false;
  return context.isEnabled(flag);
};

// Hook for multiple feature flags with memoization
export const useMultipleFeatureFlags = (
  flags: (keyof FeatureFlags)[]
): Record<string, boolean> => {
  const { isEnabled } = useFeatureFlags();

  return useMemo(() => {
    return flags.reduce((acc, flag) => {
      acc[flag] = isEnabled(flag);
      return acc;
    }, {} as Record<string, boolean>);
  }, [flags, isEnabled]);
};
